import { type ChannelAlias, fromBackendChannel, toBackendChannel } from "../channel-map";
import { type ApiFetchContext, apiFetch } from "../client";
import { buildCartListUrl, type CartListInput } from "../path-adapter";
import type {
  AddCartInput,
  CartItem,
  CartItemRaw,
  CartPlatformGroup,
  CartResult,
  CartResultRaw,
  CartShopGroupRaw,
  EditCartInput,
} from "../types/cart";
// Reuse product.ts's shared lang-threading shape rather than redefining it —
// avoids a duplicate `LangOpts` export from the package barrel (index.ts).
import type { LangOpts } from "./product";

function normalizeItem(raw: CartItemRaw): CartItem {
  return {
    id: raw.id,
    skuNo: raw.skuNo,
    spuNo: raw.spuNo,
    channel: fromBackendChannel(raw.channel),
    title: raw.subject,
    imageUrl: raw.mainImg,
    detailUrl: raw.detailUrl,
    quantity: raw.quantity ?? 1,
    startQuantity: raw.startQuantity ?? 1,
    priceRubKopecks: Math.round((raw.price ?? 0) * 100),
    originalPriceRubKopecks:
      raw.originalPrice != null && raw.originalPrice > 0
        ? Math.round(raw.originalPrice * 100)
        : null,
    originalCnyFen:
      raw.originalPriceCNY != null && raw.originalPriceCNY > 0
        ? Math.round(raw.originalPriceCNY * 100)
        : null,
    totalPriceRubKopecks: raw.totalPrice != null ? Math.round(raw.totalPrice * 100) : null,
    expressFeeRubKopecks: Math.round((raw.expressFee ?? 0) * 100),
    expressFeeCnyFen:
      raw.expressFeeCny != null && raw.expressFeeCny > 0
        ? Math.round(raw.expressFeeCny * 100)
        : null,
    // 运费重构(aps-boot #486):后端实时算运费失败时置 true → 前端显示
    // “此商品运费需买手确认” 而非金额(绝不显示 0)。
    expressFeeNeedConfirm: raw.expressFeeNeedConfirm === true,
    skuRemarks: raw.skuRemarks,
    skuAttrEnumFormatList: raw.skuAttrEnumFormatList ?? null,
    isSelected: (raw.isSelected ?? 0) > 0,
    isExpired: (raw.isExpire ?? 0) > 0,
    shopCode: raw.shopCode,
    shopName: raw.shopName,
    shopLogoUrl: raw.logoUrl,
  };
}

function groupByPlatform(shopGroupsRaw: CartShopGroupRaw[]): CartPlatformGroup[] {
  const map = new Map<ChannelAlias, CartPlatformGroup>();
  for (const sg of shopGroupsRaw) {
    const channel = fromBackendChannel(sg.channel);
    const items = (sg.itemList ?? []).map(normalizeItem);
    if (items.length === 0) continue;
    let pg = map.get(channel);
    if (!pg) {
      pg = { channel, shops: [], itemCount: 0 };
      map.set(channel, pg);
    }
    pg.shops.push({
      channel,
      shopCode: sg.shopCode,
      shopName: sg.shopName,
      shopLogoUrl: sg.logoUrl,
      items,
    });
    pg.itemCount += items.length;
  }
  return Array.from(map.values());
}

export async function queryCart(
  ctx: ApiFetchContext,
  authToken: string,
  input: CartListInput = {},
  opts: LangOpts = {},
): Promise<CartResult> {
  // Thread the backend `lang` as both a query parameter AND header so item
  // `subject` titles are localized to the active UI locale. Some backend
  // handlers read from the query string rather than headers.
  const raw = await apiFetch<CartResultRaw>(
    ctx,
    buildCartListUrl(opts.lang !== undefined ? { ...input, lang: opts.lang } : input),
    opts.lang === undefined ? { authToken } : { authToken, lang: opts.lang },
  );
  return {
    itemCount: raw.shopCartItemCount ?? 0,
    platforms: groupByPlatform(raw.shopList ?? []),
    itemsSubTotalRubKopecks: Math.round((raw.itemsSubTotal ?? 0) * 100),
    totalSavingsRubKopecks: Math.round((raw.totalSavings ?? 0) * 100),
    totalAfterDiscountRubKopecks: Math.round((raw.totalAfterDiscount ?? 0) * 100),
    expressFeeRubKopecks: Math.round((raw.expressFee ?? 0) * 100),
    payFeeRubKopecks: Math.round((raw.payFee ?? 0) * 100),
    needToBuyAmountRubKopecks: Math.round((raw.needToBuyAmount ?? 0) * 100),
  };
}

interface AddCartVo {
  currentCartItemId: string;
  cartSize: number;
}

export async function addCart(
  ctx: ApiFetchContext,
  authToken: string,
  input: AddCartInput,
  opts: LangOpts = {},
): Promise<string> {
  const vo = await apiFetch<AddCartVo>(ctx, "/shopcart/addV2", {
    method: "POST",
    authToken,
    ...(opts.lang !== undefined ? { lang: opts.lang } : {}),
    body: {
      channel: toBackendChannel(input.channel),
      spuNo: input.spuNo,
      skuNo: input.skuNo,
      quantity: input.quantity,
      ...(input.subject ? { subject: input.subject } : {}),
      ...(input.mainImg ? { mainImg: input.mainImg } : {}),
      ...(input.detailUrl ? { detailUrl: input.detailUrl } : {}),
      ...(input.skuRemarks ? { skuRemarks: input.skuRemarks } : {}),
    },
  });
  return vo.currentCartItemId;
}

export async function editCart(
  ctx: ApiFetchContext,
  authToken: string,
  input: EditCartInput,
): Promise<void> {
  // Backend editV2 returns a bare `Result.OK()` (no `result`/`data` payload).
  // Without `allowEmptyData` the null result makes `apiFetch` throw
  // BAD_ENVELOPE → the mutation's `onSuccess` never runs → the cart never
  // refetches → the quantity stepper's +/- silently do nothing (#432). Same
  // empty-envelope contract that #261 fixed for `/shopcart/edit/service`.
  await apiFetch<void>(ctx, "/shopcart/editV2", {
    method: "PUT",
    authToken,
    allowEmptyData: true,
    body: {
      id: input.id,
      // Backend editV2 requires a non-blank channel; omitting it returns
      // Result.error("Product source channel, required!") → mutation fails (#150).
      channel: toBackendChannel(input.channel),
      ...(input.quantity != null ? { quantity: input.quantity } : {}),
      ...(input.isSelected != null ? { isSelected: input.isSelected ? 1 : 0 } : {}),
    },
  });
}

/** #626 buyNowV2 — 一次请求建/复用购物车行并设置精确数量。
 *  入参与 addV2 相同;返回 currentCartItemId(与 addV2 一致),
 *  但数量已由后端锁定到 input.quantity,无需再补一次 editV2。*/
export async function buyNow(
  ctx: ApiFetchContext,
  authToken: string,
  input: AddCartInput,
  opts: LangOpts = {},
): Promise<string> {
  interface BuyNowVo {
    currentCartItemId: string;
  }
  const vo = await apiFetch<BuyNowVo>(ctx, "/shopcart/buyNowV2", {
    method: "POST",
    authToken,
    ...(opts.lang !== undefined ? { lang: opts.lang } : {}),
    body: {
      channel: toBackendChannel(input.channel),
      spuNo: input.spuNo,
      skuNo: input.skuNo,
      quantity: input.quantity,
      ...(input.subject ? { subject: input.subject } : {}),
      ...(input.mainImg ? { mainImg: input.mainImg } : {}),
      ...(input.detailUrl ? { detailUrl: input.detailUrl } : {}),
      ...(input.skuRemarks ? { skuRemarks: input.skuRemarks } : {}),
    },
  });
  return vo.currentCartItemId;
}

export async function deleteCartBatch(
  ctx: ApiFetchContext,
  authToken: string,
  ids: string[],
): Promise<string> {
  return apiFetch<string>(ctx, "/shopcart/deleteBatch", {
    method: "DELETE",
    authToken,
    // Backend signature is `@RequestBody List<String> ids` — it expects a raw
    // JSON array `["id1","id2"]`, not an object `{ids:[...]}`. Wrapping it makes
    // Spring's message converter fail → 400 INVALID_MESSAGE (#150).
    body: ids,
  });
}
